import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Loader2,
  Timer,
  Users,
  Plus,
  Wand2,
  Globe,
  AlertCircle,
  CheckCircle,
  X,
  Youtube,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useLocationStore } from '@/store/location';
import {
  useSearchRecipes,
  useGenerateRecipe,
  useImportRecipe,
  useCreateTemplate,
  aiResultToTemplateInput,
  type AIRecipeResult,
} from '@/hooks/useStandardizedTemplates';

type DiscoveryMode = 'search' | 'generate' | 'import';

const modeInfo = {
  search: {
    icon: Search,
    title: 'Search Recipes',
    description: 'Find recipe variations by searching for a dish name',
    placeholder: 'e.g., "Ackee and Saltfish", "Brown Stew Chicken", "Curry Goat"',
    buttonText: 'Search Recipes',
  },
  generate: {
    icon: Wand2,
    title: 'Generate Recipe',
    description: 'Create a new recipe from scratch using AI',
    placeholder: 'e.g., "Spicy Jerk Chicken with Mango Salsa"',
    buttonText: 'Generate Recipe',
  },
  import: {
    icon: Globe,
    title: 'Import from URL',
    description: 'Import and clean up a recipe from any website',
    placeholder: 'https://example.com/recipe/...',
    buttonText: 'Import Recipe',
  },
};

const cuisineOptions = [
  'Any',
  'American',
  'Caribbean',
  'Chinese',
  'French',
  'Indian',
  'Italian',
  'Japanese',
  'Korean',
  'Mediterranean',
  'Mexican',
  'Middle Eastern',
  'Thai',
  'Vietnamese',
];

const dietaryOptions = [
  'Vegan',
  'Vegetarian',
  'Gluten-Free',
  'Dairy-Free',
  'Nut-Free',
  'Keto',
  'Paleo',
  'Low-Carb',
  'Low-Sodium',
];

export default function RecipeDiscovery() {
  const navigate = useNavigate();
  const { currentLocation } = useLocationStore();
  const [mode, setMode] = useState<DiscoveryMode>('search');
  const [query, setQuery] = useState('');
  const [cuisineType, setCuisineType] = useState('Any');
  const [dietaryRestrictions, setDietaryRestrictions] = useState<string[]>([]);
  const [servings, setServings] = useState(4);
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');
  const [results, setResults] = useState<AIRecipeResult[]>([]);
  const [selectedResult, setSelectedResult] = useState<AIRecipeResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const searchRecipes = useSearchRecipes();
  const generateRecipe = useGenerateRecipe();
  const importRecipe = useImportRecipe();
  const createTemplate = useCreateTemplate();

  const isLoading =
    searchRecipes.isPending || generateRecipe.isPending || importRecipe.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResults([]);
    setSelectedResult(null);

    if (!query.trim()) {
      setError('Please enter a search term or URL');
      return;
    }

    try {
      if (mode === 'search') {
        const recipes = await searchRecipes.mutateAsync({
          query,
          cuisineType: cuisineType !== 'Any' ? cuisineType : undefined,
          dietaryRestrictions:
            dietaryRestrictions.length > 0 ? dietaryRestrictions : undefined,
        });
        setResults(recipes);
      } else if (mode === 'generate') {
        const recipe = await generateRecipe.mutateAsync({
          name: query,
          cuisineType: cuisineType !== 'Any' ? cuisineType : undefined,
          dietaryRestrictions:
            dietaryRestrictions.length > 0 ? dietaryRestrictions : undefined,
          servings,
          difficulty,
        });
        setResults([recipe]);
        setSelectedResult(recipe);
      } else if (mode === 'import') {
        const recipe = await importRecipe.mutateAsync({ url: query });
        setResults([recipe]);
        setSelectedResult(recipe);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleSaveAsTemplate = async (recipe: AIRecipeResult) => {
    try {
      const templateInput = aiResultToTemplateInput(
        recipe,
        mode === 'import' ? 'imported' : 'ai_generated',
        mode === 'import' ? query : undefined,
        currentLocation?.id
      );

      const newTemplate = await createTemplate.mutateAsync(templateInput);
      setSuccessMessage(`"${recipe.name}" saved as template!`);
      
      setTimeout(() => {
        navigate(`/standardization/${newTemplate.id}`);
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template');
    }
  };

  const toggleDietaryRestriction = (restriction: string) => {
    setDietaryRestrictions((prev) =>
      prev.includes(restriction)
        ? prev.filter((r) => r !== restriction)
        : [...prev, restriction]
    );
  };

  const currentModeInfo = modeInfo[mode];

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Mode Selector */}
      <div className="bg-card rounded-xl border p-1.5 flex gap-1">
        {(Object.keys(modeInfo) as DiscoveryMode[]).map((modeKey) => {
          const info = modeInfo[modeKey];
          const isActive = mode === modeKey;
          return (
            <button
              key={modeKey}
              onClick={() => {
                setMode(modeKey);
                setQuery('');
                setResults([]);
                setError(null);
              }}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all',
                isActive
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-muted/70'
              )}
            >
              <info.icon className="h-4 w-4" />
              {info.title}
            </button>
          );
        })}
      </div>

      {/* Search Form */}
      <div className="bg-card rounded-xl border p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Main Input */}
          <div>
            <Label className="mb-2 block">{currentModeInfo.title}</Label>
            <div className="relative">
              <currentModeInfo.icon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type={mode === 'import' ? 'url' : 'text'}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={currentModeInfo.placeholder}
                className="pl-12 h-12 text-lg"
              />
            </div>
            <p className="mt-1.5 text-sm text-muted-foreground">
              {currentModeInfo.description}
            </p>
          </div>

          {/* Additional Options */}
          {mode !== 'import' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
              {/* Cuisine Type */}
              <div>
                <Label className="mb-2 block">Cuisine Type</Label>
                <Select value={cuisineType} onValueChange={setCuisineType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {cuisineOptions.map((cuisine) => (
                      <SelectItem key={cuisine} value={cuisine}>
                        {cuisine}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Servings (generate mode only) */}
              {mode === 'generate' && (
                <div>
                  <Label className="mb-2 block">Servings</Label>
                  <Input
                    type="number"
                    min={1}
                    max={50}
                    value={servings}
                    onChange={(e) => setServings(parseInt(e.target.value) || 4)}
                  />
                </div>
              )}

              {/* Difficulty (generate mode only) */}
              {mode === 'generate' && (
                <div>
                  <Label className="mb-2 block">Difficulty</Label>
                  <Select
                    value={difficulty}
                    onValueChange={(v) => setDifficulty(v as 'easy' | 'medium' | 'hard')}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="easy">Easy</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="hard">Hard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Dietary Restrictions */}
              <div className="md:col-span-2">
                <Label className="mb-2 block">Dietary Restrictions (Optional)</Label>
                <div className="flex flex-wrap gap-2">
                  {dietaryOptions.map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => toggleDietaryRestriction(option)}
                      className={cn(
                        'px-3 py-1.5 text-sm rounded-full border transition-colors',
                        dietaryRestrictions.includes(option)
                          ? 'bg-primary/10 border-primary/30 text-primary'
                          : 'border-border text-muted-foreground hover:bg-muted'
                      )}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <p className="text-sm">{error}</p>
              <button
                type="button"
                onClick={() => setError(null)}
                className="ml-auto p-1 hover:bg-destructive/20 rounded"
                aria-label="Dismiss error"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="flex items-center gap-2 p-3 bg-green-500/10 text-green-600 dark:text-green-400 rounded-lg">
              <CheckCircle className="h-5 w-5 flex-shrink-0" />
              <p className="text-sm">{successMessage}</p>
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isLoading || !query.trim()}
            className="w-full h-12"
            size="lg"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                {mode === 'search'
                  ? 'Searching...'
                  : mode === 'generate'
                  ? 'Generating...'
                  : 'Importing...'}
              </>
            ) : (
              <>
                <currentModeInfo.icon className="h-5 w-5 mr-2" />
                {currentModeInfo.buttonText}
              </>
            )}
          </Button>
        </form>
      </div>

      {/* Results */}
      {results.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">
            {mode === 'search'
              ? `Found ${results.length} Recipe Variations`
              : 'Generated Recipe'}
          </h2>

          <div className="space-y-4">
            {results.map((recipe, index) => (
              <div
                key={index}
                className={cn(
                  'bg-card rounded-xl border-2 overflow-hidden transition-all cursor-pointer',
                  selectedResult === recipe
                    ? 'border-primary shadow-lg'
                    : 'border-border hover:border-primary/50'
                )}
                onClick={() =>
                  setSelectedResult(selectedResult === recipe ? null : recipe)
                }
              >
                <div className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold">
                        {recipe.name}
                      </h3>
                      {recipe.cuisineType && (
                        <p className="text-sm text-muted-foreground">
                          {recipe.cuisineType}
                          {recipe.category && ` • ${recipe.category}`}
                        </p>
                      )}
                      <p className="mt-2 text-sm text-muted-foreground">
                        {recipe.description}
                      </p>

                      {/* Meta Info */}
                      <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                        {(recipe.prepTimeMinutes || recipe.cookTimeMinutes) && (
                          <span className="flex items-center gap-1">
                            <Timer className="h-4 w-4" />
                            {(recipe.prepTimeMinutes || 0) +
                              (recipe.cookTimeMinutes || 0)}{' '}
                            min total
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          {recipe.servings} servings
                        </span>
                        {recipe.difficultyLevel && (
                          <span
                            className={cn(
                              'px-2 py-0.5 rounded-full text-xs font-medium',
                              recipe.difficultyLevel === 'easy' &&
                                'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
                              recipe.difficultyLevel === 'medium' &&
                                'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
                              recipe.difficultyLevel === 'hard' &&
                                'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                            )}
                          >
                            {recipe.difficultyLevel}
                          </span>
                        )}
                      </div>

                      {/* Dietary Tags */}
                      {recipe.dietaryTags && recipe.dietaryTags.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {recipe.dietaryTags.map((tag) => (
                            <span
                              key={tag}
                              className="px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Open YouTube search for this recipe
                          const searchQuery = encodeURIComponent(`${recipe.name} recipe`);
                          window.open(`https://www.youtube.com/results?search_query=${searchQuery}`, '_blank');
                        }}
                      >
                        <Youtube className="h-4 w-4 mr-2" />
                        Find on YouTube
                      </Button>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSaveAsTemplate(recipe);
                        }}
                        disabled={createTemplate.isPending}
                      >
                        {createTemplate.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Plus className="h-4 w-4 mr-2" />
                        )}
                        Save as Template
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {selectedResult === recipe && (
                  <div className="border-t p-5 bg-muted/30">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Ingredients */}
                      <div>
                        <h4 className="text-sm font-semibold mb-3">
                          Ingredients ({recipe.ingredients.length})
                        </h4>
                        <ul className="space-y-2">
                          {recipe.ingredients.map((ing, i) => (
                            <li
                              key={i}
                              className="flex items-start gap-2 text-sm text-muted-foreground"
                            >
                              <span className="h-1.5 w-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                              <span>
                                <strong className="text-foreground">
                                  {ing.quantity} {ing.unit}
                                </strong>{' '}
                                {ing.name}
                                {ing.notes && (
                                  <span className="opacity-70"> ({ing.notes})</span>
                                )}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Instructions */}
                      <div>
                        <h4 className="text-sm font-semibold mb-3">
                          Instructions ({recipe.instructions.length} steps)
                        </h4>
                        <ol className="space-y-3">
                          {recipe.instructions.map((inst) => (
                            <li
                              key={inst.step_number}
                              className="flex gap-3 text-sm text-muted-foreground"
                            >
                              <span className="flex-shrink-0 h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">
                                {inst.step_number}
                              </span>
                              <span>
                                {inst.description}
                                {inst.duration_minutes && (
                                  <span className="opacity-70 ml-1">
                                    ({inst.duration_minutes} min)
                                  </span>
                                )}
                              </span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

